/** @jsx jsx */

import { isFinite, isNaN, isNumber } from 'lodash';

import { jsx } from '@emotion/core';
import { abbreviateNumber, relativeRound } from '@zapier/common-utils';

type NumberProps = {
  /** Number value */
  children: number;
  /**
   * Label to show when value is infinite
   *
   * @default Infinity
   */
  infinityLabel?: string;
  /**
   * Abbreviate number using SI suffix
   *
   * @default false
   */
  isAbbreviated?: boolean;
  /**
   * Use friendly relative rounding
   *
   * @default false
   */
  isFriendly?: boolean;
  /**
   * Treat -1 as infinite
   *
   * @default false
   */
  isNegativeOneInfinity?: boolean;
  /**
   * Maximum number of decimals to show
   *
   * @default 2
   */
  maxDecimals?: number;
  /**
   * Minimum number of decimals to show
   *
   * @default 0
   */
  minDecimals?: number;
  /**
   * Label to show when value is not a number
   *
   * @default NaN
   */
  nanLabel?: string;
};

/**
 * Formats a number value for display
 */
const Number = (props: NumberProps) => {
  const {
    children,
    infinityLabel = 'Infinity',
    isAbbreviated = false,
    isFriendly = false,
    isNegativeOneInfinity = false,
    maxDecimals = 2,
    minDecimals = 0,
    nanLabel = 'NaN',
  } = props;

  let displayValue: string | number | null = children;

  // Non-number values
  if (isNaN(displayValue) || !isNumber(displayValue)) {
    return <span>{nanLabel}</span>;
  }

  // Infinite number values
  if (
    !isFinite(displayValue) ||
    // special case for treating -1 as Infinity
    (isNegativeOneInfinity && displayValue === -1)
  ) {
    return <span>{infinityLabel}</span>;
  }

  // Finite Number values
  if (isFriendly) {
    // Relative round first if required
    displayValue = relativeRound(displayValue);
  }

  if (isAbbreviated) {
    displayValue = abbreviateNumber(displayValue);
  } else {
    // Format to Locale if we are not abbreviating
    displayValue = displayValue.toLocaleString(undefined, {
      maximumFractionDigits: maxDecimals,
      minimumFractionDigits: minDecimals,
    });
  }

  return (
    <span title={isAbbreviated ? children.toLocaleString() : undefined}>
      {displayValue}
    </span>
  );
};

export default Number;
